import React, { useEffect, useState } from "react";
import { API } from "@/lib/api";
import resolveImageUrl from "@/lib/imageUrl";

// Media Library — visual grid of every uploaded photo.
// Admin can upload new, edit alt text and delete (blocked if in use).
export default function MediaLibrary({ authed }) {
  const [items, setItems] = useState(null);
  const [q, setQ] = useState("");
  const [editing, setEditing] = useState(null); // full row
  const [uploading, setUploading] = useState(false);
  const [msg, setMsg] = useState("");
  const [deleteInfo, setDeleteInfo] = useState(null); // {row, usage}

  const load = async () => {
    const r = await authed.get(`/admin/uploads/list?limit=500${q ? `&q=${encodeURIComponent(q)}` : ""}`);
    setItems(r.data.items || []);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);
  useEffect(() => { const id = setTimeout(load, 250); return () => clearTimeout(id); /* eslint-disable-next-line */ }, [q]);

  const uploadFiles = async (files) => {
    if (!files || !files.length) return;
    setUploading(true); setMsg("");
    try {
      for (const f of Array.from(files)) {
        const form = new FormData();
        form.append("file", f);
        await authed.post("/admin/uploads/image", form, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      }
      setMsg(`Uploaded ${files.length} photo${files.length === 1 ? "" : "s"}.`);
      await load();
    } catch (ex) {
      setMsg(ex.response?.data?.detail || "Upload failed");
    } finally { setUploading(false); }
  };

  const saveAlt = async (row, alt_text) => {
    await authed.patch(`/admin/uploads/${row.id}`, { alt_text });
    setEditing(null);
    await load();
  };

  const askDelete = async (row) => {
    const r = await authed.get(`/admin/uploads/${row.id}/usage`);
    setDeleteInfo({ row, usage: r.data });
  };

  const doDelete = async () => {
    if (!deleteInfo) return;
    try {
      await authed.delete(`/admin/uploads/${deleteInfo.row.id}`);
      setDeleteInfo(null);
      setMsg("Image deleted.");
      await load();
    } catch (ex) {
      setMsg(ex.response?.data?.detail?.message || "Delete blocked (image in use).");
    }
  };

  if (items === null) return <div className="text-ink/50 text-sm">Loading…</div>;

  return (
    <div className="space-y-6" data-testid="media-library">
      <section className="border border-line rounded-xl p-6 bg-white">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <div className="label-mono text-terracotta text-[10px]">MEDIA LIBRARY</div>
            <h2 className="font-editorial text-2xl md:text-3xl tracking-tighter mt-1">Photographs</h2>
            <p className="mt-2 text-xs text-ink/60">{items.length} photo{items.length === 1 ? "" : "s"}. Upload once, reuse across the website.</p>
          </div>
          <div className="flex gap-2">
            <button onClick={async () => {
                       if (!confirm("Populate the library with every photo the site currently uses? This won't change the live pages — it just makes those images pickable in the CMS. Safe to run again later.")) return;
                       setUploading(true); setMsg("");
                       try {
                         const r = await authed.post("/admin/uploads/seed-demo", {});
                         setMsg(`Rehearsal seed loaded — ${r.data.created} added, ${r.data.already_present} already present.`);
                         await load();
                       } catch (ex) {
                         setMsg(ex.response?.data?.detail || "Seed failed");
                       } finally { setUploading(false); }
                     }}
                     data-testid="media-seed-demo"
                     disabled={uploading}
                     className="border border-ink text-ink px-4 py-2 text-xs uppercase tracking-widest hover:bg-ink hover:text-white transition-colors disabled:opacity-50">
              Seed rehearsal set
            </button>
            <label className={`bg-terracotta text-white px-4 py-2 text-xs uppercase tracking-widest cursor-pointer hover:bg-terracotta-dark ${uploading ? "opacity-50 pointer-events-none" : ""}`}>
              <input type="file" accept="image/*" multiple className="hidden"
                     onChange={e => uploadFiles(e.target.files)}
                     data-testid="media-upload-input" />
              {uploading ? "Working…" : "Upload photos"}
            </label>
          </div>
        </div>
        <div className="mt-4 flex gap-3">
          <input value={q} onChange={e => setQ(e.target.value)} placeholder="Search filename or alt…"
                 data-testid="media-search"
                 className="w-full max-w-md border border-line px-3 py-2 text-sm" />
        </div>
        {msg && <div className="mt-3 text-xs text-ink/60">{msg}</div>}
      </section>

      {items.length === 0 ? (
        <div className="text-ink/40 text-sm py-16 text-center border border-dashed border-line rounded-xl">No images yet. Upload one to get started.</div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4" data-testid="media-grid">
          {items.map(it => (
            <div key={it.id} className="border border-line hover:border-ink/30 transition-colors" data-testid={`media-card-${it.id}`}>
              <div className="aspect-square bg-line/20 overflow-hidden relative">
                <img src={resolveImageUrl(it.url)} alt={it.alt_text || it.filename}
                     className="w-full h-full object-cover"
                     onError={e => { e.currentTarget.style.display = "none"; e.currentTarget.nextSibling.style.display = "flex"; }} />
                <div className="absolute inset-0 hidden items-center justify-center flex-col text-ink/40 text-[10px] uppercase tracking-widest bg-line/10">
                  <div className="text-2xl mb-1">⚠</div>
                  <div>file missing</div>
                </div>
              </div>
              <div className="p-3">
                <div className="text-[11px] truncate font-mono text-ink/70">{it.filename}</div>
                <div className="text-[10px] text-ink/50 truncate italic min-h-[14px]">{it.alt_text || "no alt text"}</div>
                <div className="mt-2 flex gap-1.5">
                  <button onClick={() => setEditing(it)} data-testid={`media-edit-${it.id}`}
                          className="flex-1 text-[10px] uppercase tracking-widest border border-line px-2 py-1 hover:border-ink">Edit alt</button>
                  <button onClick={() => askDelete(it)} data-testid={`media-delete-${it.id}`}
                          className="text-[10px] uppercase tracking-widest border border-line/60 px-2 py-1 text-ink/60 hover:text-red-600 hover:border-red-500">Delete</button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {editing && (
        <AltEditor row={editing} onClose={() => setEditing(null)} onSave={saveAlt} />
      )}
      {deleteInfo && (
        <DeleteConfirm info={deleteInfo} onCancel={() => setDeleteInfo(null)} onConfirm={doDelete} />
      )}
    </div>
  );
}

function AltEditor({ row, onClose, onSave }) {
  const [alt, setAlt] = useState(row.alt_text || "");
  return (
    <div className="fixed inset-0 z-[70] bg-ink/60 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white w-full max-w-md rounded-xl shadow-2xl" onClick={e => e.stopPropagation()}>
        <div className="p-5 border-b border-line">
          <div className="label-mono text-terracotta text-[10px]">ALT TEXT</div>
          <h3 className="font-editorial text-xl tracking-tighter">{row.filename}</h3>
        </div>
        <div className="p-5">
          <img src={resolveImageUrl(row.url)} alt="" className="w-full h-40 object-cover mb-4 border border-line/50" />
          <label className="label-mono text-ink/60 text-[10px]">Describe this photo (helps screen readers & SEO)</label>
          <textarea value={alt} onChange={e => setAlt(e.target.value)} rows={3}
                    data-testid="alt-editor-input"
                    className="mt-1 w-full border border-line px-3 py-2 text-sm" />
        </div>
        <div className="p-5 border-t border-line flex justify-between gap-3">
          <button onClick={onClose} className="text-ink/60 hover:text-ink px-3 py-2 text-sm uppercase tracking-widest">Cancel</button>
          <button onClick={() => onSave(row, alt)} data-testid="alt-editor-save"
                  className="bg-terracotta hover:bg-terracotta-dark text-white px-5 py-2 text-sm uppercase tracking-widest">Save</button>
        </div>
      </div>
    </div>
  );
}

function DeleteConfirm({ info, onCancel, onConfirm }) {
  const inUse = info.usage.count > 0;
  return (
    <div className="fixed inset-0 z-[70] bg-ink/60 flex items-center justify-center p-4" onClick={onCancel}>
      <div className="bg-white w-full max-w-md rounded-xl shadow-2xl" onClick={e => e.stopPropagation()}>
        <div className="p-5 border-b border-line">
          <div className="label-mono text-terracotta text-[10px]">DELETE PHOTO</div>
          <h3 className="font-editorial text-xl tracking-tighter">{info.row.filename}</h3>
        </div>
        <div className="p-5">
          <img src={resolveImageUrl(info.row.url)} alt="" className="w-full h-40 object-cover mb-4 border border-line/50" />
          {inUse ? (
            <>
              <div className="text-sm text-red-600 font-medium">Cannot delete — this image is in use.</div>
              <div className="mt-2 text-xs text-ink/70">Replace it in the following sections first, then try again:</div>
              <ul className="mt-2 text-xs text-ink/80 space-y-1">
                {info.usage.used_in.map(u => (
                  <li key={u.id} className="border-l-2 border-terracotta/50 pl-2">
                    <span className="label-mono text-[9px] text-terracotta">{u.kind}</span> · {u.title || u.slug}
                  </li>
                ))}
              </ul>
            </>
          ) : (
            <div className="text-sm text-ink/70">This photo isn't used anywhere. Delete it permanently?</div>
          )}
        </div>
        <div className="p-5 border-t border-line flex justify-between gap-3">
          <button onClick={onCancel} className="text-ink/60 hover:text-ink px-3 py-2 text-sm uppercase tracking-widest">Cancel</button>
          {!inUse && (
            <button onClick={onConfirm} data-testid="media-delete-confirm"
                    className="bg-red-600 hover:bg-red-700 text-white px-5 py-2 text-sm uppercase tracking-widest">Delete</button>
          )}
        </div>
      </div>
    </div>
  );
}
